import * as React from "react";
import { useForm } from "antd/lib/form/Form";
import { Form, Input, Select } from "antd";
import { I18N_NS } from "Config/i18n";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "Components/General/Button";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";

interface AddNavigationFormProps extends ModalComponentProps {
  initialData?: API_ViewNavigation;
  viewMetadata: API_ViewInformation["metadata"];
  submit: (data: API_ViewNavigation) => Promise<void>;
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 4 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 20 },
  },
};
const formItemLayoutWithOutLabel = {
  wrapperCol: {
    xs: { span: 24, offset: 0 },
    sm: { span: 20, offset: 4 },
  },
};

export const AddNavigationForm = ({ viewMetadata = [], submit, initialData, hideModal }: AddNavigationFormProps) => {
  const [loading, setLoading] = React.useState(false);
  const [form] = useForm();
  const { t } = useTranslation(I18N_NS.PROJECT);
  // leave only metadata with selectable value range
  const valueRangeMetadata = viewMetadata.filter((item) => item.format.toLowerCase() === "w");

  const handleFinish = async (values) => {
    try {
      setLoading(true);
      const formData: API_ViewNavigation = {
        id: initialData?.id, // pass navigationId through formData to extract it in ViewsFoldersStore for request url
        definitions: values.definitions,
        name: values.name,
      };
      await submit(formData);
      hideModal();
      const message = initialData ? t("View navigation edited") : t("View navigation created");
      notificationSuccess(t("Success"), message);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} onFinish={handleFinish} initialValues={initialData || { definitions: [] }}>
      <Form.Item
        label={t("Name")}
        {...formItemLayout}
        name="name"
        rules={[{ required: true, message: t("This field is required") }]}
      >
        <Input autoFocus={true} placeholder={t("Name")} />
      </Form.Item>
      <Form.List name="definitions">
        {(fields, { add, remove }) => {
          const selectedMetadata = form.getFieldValue("definitions");
          // remove already picked metadata
          const metadataOpts = valueRangeMetadata.filter((item) => !selectedMetadata.includes(item.definitionId));
          return (
            <>
              {fields.map((field, index) => (
                <Form.Item
                  {...(index === 0 ? formItemLayout : formItemLayoutWithOutLabel)}
                  label={index === 0 ? t("Metadata") : ""}
                  required={false}
                  key={field.key}
                >
                  <Form.Item
                    {...field}
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      {
                        required: true,
                        message:
                          index === 0 ? t("Please select metadata") : t("Please select metadata or delete this field."),
                      },
                    ]}
                    noStyle
                  >
                    <Select placeholder={t("Metadata")} style={{ width: "60%" }}>
                      {metadataOpts.map((item) => (
                        <Select.Option key={item.key} value={item.key}>
                          {item.key}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  {fields.length > 1 ? (
                    <FontAwesomeIcon
                      icon={["far", "minus-circle"]}
                      fixedWidth={true}
                      className="dynamic-delete-button"
                      onClick={() => remove(field.name)}
                    />
                  ) : null}
                </Form.Item>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} style={{ width: "100%" }} faIcon={"plus"}>
                  {t("Add field")}
                </Button>
              </Form.Item>
            </>
          );
        }}
      </Form.List>
      <div className="text-right">
        <Button type="primary" htmlType="submit" loading={loading}>
          {t("Save")}
        </Button>
      </div>
    </Form>
  );
};
